import {
  collapseColumn,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  SelectField,
  simpleHeader,
  useBreakpoint,
  useSessionStorage,
} from "@homebound/beam";

import { emptyCellDash, priceCell } from "src/components";
import { InvoicePage_BillLineItemsFragment } from "src/generated/graphql-types";
import { TableActions } from "src/routes/layout/TableActions";
import { foldEnum } from "src/utils";

enum ItemsGroupBy {
  Templates = "template",
  ItemCode = "itemCode",
}

export function InvoiceTable(props: { lineItems: InvoicePage_BillLineItemsFragment[] }) {
  const { lineItems } = props;
  const { mdAndDown: mediumAndSmaller } = useBreakpoint();
  const itemTemplateItems = lineItems.map((li) => li.commitmentLineItem.projectItem.itemTemplateItem).compact();
  const [templatesGroupBy, setTemplatesGroupBy] = useSessionStorage("templatesTable", ItemsGroupBy.ItemCode);

  return (
    <div css={Css.overflowScroll.mt6.$}>
      <div css={Css.df.jcsb.mb4.fdr.if(mediumAndSmaller).fdc.$}>
        <div css={Css.lgSb.if(mediumAndSmaller).mb2.$}>Completed Item Codes</div>
        <TableActions>
          <div>
            <SelectField
              compact
              sizeToContent
              label="Group by"
              labelStyle={"inline"}
              options={[
                ...(itemTemplateItems.nonEmpty ? [{ id: ItemsGroupBy.Templates, name: "Plan and Option" }] : []),
                { id: ItemsGroupBy.ItemCode, name: "Item Code" },
              ]}
              getOptionValue={(o) => o.id}
              getOptionLabel={(o) => o.name}
              value={templatesGroupBy}
              onSelect={(gb) => gb && setTemplatesGroupBy(gb)}
            />
          </div>
        </TableActions>
      </div>
      <GridTable
        as="table"
        rows={rows(lineItems, templatesGroupBy)}
        columns={columns()}
        style={{ bordered: false, allWhite: true }}
        rowStyles={{ header: { cellCss: Css.gray600.$ }, total: { cellCss: Css.smSb.mt2.$ } }}
      />
    </div>
  );
}

type HeaderRow = { kind: "header" };
type GroupRow = {
  kind: "group";
  data: string;
};
type ItemRow = {
  kind: "item";
  data: InvoicePage_BillLineItemsFragment;
};
type TotalRow = {
  kind: "total";
  data: number;
};
export type Row = HeaderRow | GroupRow | ItemRow | TotalRow;

function rows(lineItems: InvoicePage_BillLineItemsFragment[], groupedBy: ItemsGroupBy): GridDataRow<Row>[] {
  const groupedItis = foldEnum(groupedBy, {
    template: groupByTemplate(lineItems),
    itemCode: groupByItemCode(lineItems),
  });
  return [
    simpleHeader,
    ...groupedItis,
    {
      kind: "total" as const,
      id: "total",
      data: lineItems.sum((li) => (li.bill.isTradePartnerCredit ? -li.amountInCents : li.amountInCents)),
    },
  ];
}

function groupByTemplate(lis: InvoicePage_BillLineItemsFragment[]): GridDataRow<Row>[] {
  const groupedbyTemplates = lis.groupBy((li) => li.commitmentLineItem.projectItem.baseOrOptionName);
  return Object.entries(groupedbyTemplates).map(([templateName, clis]) => {
    return {
      kind: "group" as const,
      id: templateName,
      data: templateName,
      pin: templateName.includes("Base") ? { at: "first" as const, filter: true } : undefined,
      children: clis.map((cli) => ({
        kind: "item" as const,
        id: cli.id,
        data: cli,
      })),
    };
  });
}

function groupByItemCode(lis: InvoicePage_BillLineItemsFragment[]): GridDataRow<Row>[] {
  const groupedItems = lis.groupBy((li) => li.commitmentLineItem.projectItem.item.costCode.displayName);
  return Object.entries(groupedItems).map(([itemCode, clis]) => {
    return {
      kind: "group" as const,
      id: itemCode,
      data: clis.first?.commitmentLineItem.projectItem.item.costCode.displayName ?? "-",
      children: clis.map((li) => ({
        kind: "item" as const,
        id: li.id,
        data: li,
      })),
    };
  });
}

function columns(): GridColumn<Row>[] {
  return [
    collapseColumn<Row>({ item: emptyCell }),
    column<Row>({
      id: "qty",
      header: "QTY",
      group: (data) => data,
      item: ({ commitmentLineItem: { newQuantity, quantity } }) => newQuantity || quantity || emptyCellDash,
      total: emptyCell,
      w: "200px",
    }),
    column<Row>({
      id: "uom",
      header: "UoM",
      group: emptyCell,
      item: ({
        commitmentLineItem: {
          projectItem: { itemTemplateItem, unitOfMeasure },
        },
      }) => itemTemplateItem?.unitOfMeasure.abbreviation.toLowerCase() ?? unitOfMeasure.abbreviation.toLowerCase(), // use pi if no iti
      total: emptyCell,
      w: "100px",
    }),
    column<Row>({
      id: "itemCode",
      header: "Item Code",
      group: emptyCell,
      item: ({
        commitmentLineItem: {
          projectItem: { itemTemplateItem, item },
        },
      }) => itemTemplateItem?.item.fullCode ?? item.fullCode,
      total: emptyCell,
      w: "150px",
    }),
    column<Row>({
      id: "description",
      header: "Description",
      group: emptyCell,
      item: ({
        commitmentLineItem: {
          projectItem: { itemTemplateItem, name },
        },
      }) => itemTemplateItem?.name ?? name,
      total: "Total Amount Due",
      w: "200px",
    }),
    column<Row>({
      id: "unitCost",
      header: "Unit Cost",
      group: emptyCell,
      item: (lineItem) => {
        if (lineItem.commitmentLineItem.projectItem.unitOfMeasure.useQuantity) {
          const { quantity, newQuantity, newTotalCostInCents } = lineItem.commitmentLineItem;
          return priceCell({ valueInCents: (newTotalCostInCents || 0) / (newQuantity || quantity || 1) });
        } else {
          return emptyCellDash;
        }
      },
      total: emptyCell,
      w: "140px",
    }),
    column<Row>({
      id: "totalCost",
      header: "Total Cost",
      group: emptyCell,
      item: ({ amountInCents, bill }) =>
        priceCell({ valueInCents: bill.isTradePartnerCredit ? -amountInCents : amountInCents }),
      total: (data) => priceCell({ valueInCents: data }),
      w: "160px",
    }),
  ];
}
